import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface ClaimSummaryRow {
  id: string;
  claim_name: string;
  target: string | null;
  survival: string | null;
  claim_kind?: string;
  asserting_party_id?: string | null;
  summary?: string | null;
}
export interface ClaimsSummary {
  total: number;
  live: number;
  defeated: number;
  /** Our return volley — counterclaims raised against the plaintiff. */
  counterclaim_total: number;
  counterclaim_live: number;
  claims: ClaimSummaryRow[];
  counterclaims: ClaimSummaryRow[];
}

const DEFEATED = new Set(["defeated", "dismissed", "struck", "withdrawn"]);
export function isLive(survival: string | null): boolean {
  return !DEFEATED.has((survival ?? "").toLowerCase());
}

/** Claims summary that weights the crossfire vectors on the board (live vs total). */
export function useClaimsSummary(caseId: string | undefined) {
  return useQuery<ClaimsSummary, Error>({
    queryKey: ["claims-summary", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("get_case_claims_summary", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (
        (data as ClaimsSummary) ?? {
          total: 0, live: 0, defeated: 0,
          counterclaim_total: 0, counterclaim_live: 0,
          claims: [], counterclaims: [],
        }
      );
    },
  });
}

/** A counterclaim candidate scaffolded by the Answer-forge (Rule 13 prong). */
export interface CounterclaimCandidate {
  claim_name: string;
  theory: string | null;
  grounds: string | null;
  basis: string | null;
  compulsory: boolean;
  must_prove: string[];
}
export interface CounterclaimCandidates {
  source: string | null; // when the Answer was forged
  items: CounterclaimCandidate[];
}

/** Counterclaim candidates from the latest forged Answer — prompts the editor. */
export function useCounterclaimCandidates(caseId: string | undefined) {
  return useQuery<CounterclaimCandidates, Error>({
    queryKey: ["counterclaim-candidates", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("fetch_counterclaim_candidates", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as CounterclaimCandidates) ?? { source: null, items: [] };
    },
  });
}

/** Knock a claim down (defeated) or restore it — thins/restores the volley. */
export function useSetClaimSurvival(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, { claimId: string; survival: string }>({
    mutationFn: async ({ claimId, survival }) => {
      const { error } = await berserkr().rpc("set_claim_survival", {
        p_claim_id: claimId,
        p_survival: survival,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims-summary", caseId] });
      qc.invalidateQueries({ queryKey: ["case-claims", caseId] });
    },
  });
}
